<template>
  <div>
    <b-alert v-model="isAuthError" variant="danger" class="mt-3" dismissible>{{ authError }}</b-alert>

    <div v-if="notification.message" :class="'alert ' + notification.type">
      {{ $t(notification.message) }}
    </div>

    <b-form class="p-2" @submit.prevent="tryToLogIn">
      <b-form-group id="input-group-1" :label="$t('common.email')" label-for="input-1" class="mb-3">
        <b-form-input
          id="input-1"
          v-model="email"
          type="text"
          autocomplete="username"
          :placeholder="$t('account.login.enterEmail')"
          :class="{ 'is-invalid': submitted && v$.email.$error }"
        />
        <div v-if="submitted && v$.email.$error" class="invalid-feedback">
          <span v-if="v$.email.required.$invalid">{{ $t('forms.feedback.emailRequired') }}</span>
          <span v-if="v$.email.email.$invalid">{{ $t('forms.feedback.validEmailRequired') }}</span>
        </div>
      </b-form-group>
      <BFormGroup id="input-group-2" :label="$t('account.login.password')" label-for="input-2">
        <BInputGroup>
          <BFormInput
            id="input-2"
            v-model="password"
            :placeholder="$t('account.login.enterPassword')"
            :class="{ 'is-invalid': submitted && v$.password.$error }"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password"
          />
          <b-button
            data-testid="pwd-show-btn"
            class="show-btn"
            variant="outline-secondary"
            @click="showPassword = !showPassword"
          >
            <i class="bx bxs-show" v-if="showPassword"></i>
            <i class="bx bxs-hide" v-else></i>
          </b-button>
          <div v-if="submitted && v$.password.required.$invalid" class="invalid-feedback">
            {{ $t('forms.feedback.passwordRequired') }}
          </div>
        </BInputGroup>
      </BFormGroup>
      <div class="mt-3">
        <b-button
          type="submit"
          loading-fill
          :loading="loader"
          class="btn btn-block btn-ca"
        >
          {{ $t('account.login.logIn') }}
        </b-button>
      </div>
      <p class="text-center mt-4">
        <router-link class="text-primary" :to="{ name: 'forgot' }">
          {{ $t('account.login.forgottenPassword') }}
        </router-link>
      </p>
    </b-form>
  </div>
</template>
<script>
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

export default {
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
      loader: false,
      notification: {
        message: '',
        type: '',
      },
    };
  },
  emits: ['login-success'],
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  validations: {
    email: {
      required,
      email,
    },
    password: {
      required,
    },
  },
  methods: {
    // Try to log the user in with the username
    // and password they provided.
    async tryToLogIn() {
      this.resetNotification();
      this.submitted = true;
      this.loader = true;
      // stop here if form is invalid
      this.v$.$touch();

      if (this.v$.$invalid) {
        this.loader = false;
        return;
      } else {
        try {
          const user = await this.$store.dispatch('currentUser/logIn', {
            username: this.email,
            password: this.password,
          });
          if (user) {
            this.$emit('login-success');
          }
        } catch (e) {
          this.notifyWrongCredential(e.message);
        } finally {
          this.loader = false;
        }
      }
    },
    notifyWrongCredential(message) {
      if (message === 'banned') {
        this.notification.message = 'account.login.userBanned';
        this.notification.type = 'alert-danger';
      } else if (message === 'deactivated') {
        this.notification.message = 'account.login.accountDeactivated';
        this.notification.type = 'alert-danger';
      } else {
        this.notification.message = 'account.login.invalidLoginCredential';
        this.notification.type = 'alert-danger';
      }
    },
    resetNotification() {
      this.notification.message = '';
      this.notification.type = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@versions/x/styles/variables.scss';

.show-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border-color: $gray-400;
}
</style>
